import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { saveImapConnection, testImapConnection } from '../services/mailboxService';

const presets = {
  yahoo: {
    title: 'Connect Yahoo Mail',
    note: 'Yahoo normally requires an app password instead of your regular password.',
    imapHost: 'imap.mail.yahoo.com',
    imapPort: 993,
    smtpHost: 'smtp.mail.yahoo.com',
    smtpPort: 465,
  },
  icloud: {
    title: 'Connect iCloud Mail',
    note: 'iCloud requires an app-specific password when two-factor authentication is enabled.',
    imapHost: 'imap.mail.me.com',
    imapPort: 993,
    smtpHost: 'smtp.mail.me.com',
    smtpPort: 587,
  },
  imap: {
    title: 'Connect with IMAP and SMTP',
    note: 'Enter the incoming and outgoing mail server details supplied by your email provider.',
    imapHost: '',
    imapPort: 993,
    smtpHost: '',
    smtpPort: 465,
  },
};

export function ImapConnectDialog({ open, provider = 'imap', initialEmail = '', onClose, onConnected }) {
  const preset = useMemo(() => presets[provider] || presets.imap, [provider]);
  const [values, setValues] = useState({});
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tested, setTested] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!open) return;
    setValues({
      email: initialEmail || '',
      imapHost: preset.imapHost,
      imapPort: preset.imapPort,
      imapUsername: initialEmail || '',
      imapPassword: '',
      smtpHost: preset.smtpHost,
      smtpPort: preset.smtpPort,
      smtpUsername: initialEmail || '',
      smtpPassword: '',
    });
    setTested(false);
    setMessage(null);
  }, [open, initialEmail, preset]);

  const set = (field) => (event) => {
    const value = event.target.value;
    setValues((current) => {
      const next = { ...current, [field]: value };
      if (field === 'email') {
        if (!current.imapUsername || current.imapUsername === current.email) next.imapUsername = value;
        if (!current.smtpUsername || current.smtpUsername === current.email) next.smtpUsername = value;
      }
      if (field === 'imapPassword' && !current.smtpPassword) next.smtpPassword = value;
      return next;
    });
    setTested(false);
    setMessage(null);
  };

  const settings = () => ({
    ...values,
    imapPort: Number(values.imapPort),
    smtpPort: Number(values.smtpPort),
  });

  const test = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const result = await testImapConnection(settings());
      if (!result?.success) {
        throw new Error([result?.imap?.error, result?.smtp?.error].filter(Boolean).join(' ') || 'Connection test failed.');
      }
      setTested(true);
      setMessage({ type: 'success', text: 'IMAP and SMTP connections were verified.' });
    } catch (error) {
      setTested(false);
      setMessage({ type: 'error', text: error?.message || 'Connection test failed.' });
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await saveImapConnection(provider, settings());
      await onConnected?.();
      onClose();
    } catch (error) {
      setMessage({ type: 'error', text: error?.message || 'Could not save this mailbox.' });
    } finally {
      setSaving(false);
    }
  };

  const required = values.email && values.imapHost && values.imapPort && values.imapUsername && values.imapPassword && values.smtpHost && values.smtpPort && values.smtpUsername && values.smtpPassword;

  return (
    <Dialog open={open} onClose={saving || testing ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{preset.title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.2}>
          <Alert severity="info">{preset.note}</Alert>
          <TextField label="Mailbox email" value={values.email || ''} onChange={set('email')} fullWidth />
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={8}><TextField label="IMAP host" value={values.imapHost || ''} onChange={set('imapHost')} fullWidth /></Grid>
            <Grid item xs={12} sm={4}><TextField label="IMAP port" type="number" value={values.imapPort || ''} onChange={set('imapPort')} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="IMAP username" value={values.imapUsername || ''} onChange={set('imapUsername')} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Password or app password" type="password" value={values.imapPassword || ''} onChange={set('imapPassword')} fullWidth /></Grid>
            <Grid item xs={12} sm={8}><TextField label="SMTP host" value={values.smtpHost || ''} onChange={set('smtpHost')} fullWidth /></Grid>
            <Grid item xs={12} sm={4}><TextField label="SMTP port" type="number" value={values.smtpPort || ''} onChange={set('smtpPort')} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="SMTP username" value={values.smtpUsername || ''} onChange={set('smtpUsername')} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="SMTP password" type="password" value={values.smtpPassword || ''} onChange={set('smtpPassword')} fullWidth /></Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary">
            Credentials are sent over HTTPS and encrypted before being stored. Nudge never returns them to the browser.
          </Typography>
          {message && <Alert severity={message.type}>{message.text}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving || testing}>Cancel</Button>
        <Button variant="outlined" onClick={test} disabled={!required || saving || testing} startIcon={testing ? <CircularProgress size={16} /> : null}>
          {testing ? 'Testing…' : tested ? 'Test again' : 'Test connection'}
        </Button>
        <Button variant="contained" onClick={save} disabled={!required || !tested || saving || testing} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          {saving ? 'Saving…' : 'Connect mailbox'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
